import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, CheckCircle, XCircle, MapPin, Edit } from 'lucide-react';
import ProfilePicture from '@/components/ProfilePicture';
import { getMonthOptions, formatMonth } from './useContributorAdmin';

const PayoutsManager = ({ 
  selectedMonth, 
  setSelectedMonth, 
  monthlyRevenue, 
  payouts, 
  settings,
  onFinalizeRevenue,
  onGeneratePayouts,
  onUpdatePayout,
  onOpenManualRevenue
}) => {
  return (
    <div className="space-y-4">
      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Membership Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-white">
              ${monthlyRevenue?.total_membership_revenue?.toFixed(2) || '0.00'}
            </p>
            {monthlyRevenue?.is_manual && (
              <Badge variant="outline" className="mt-1 text-xs">Manual Entry</Badge>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">{settings.revenue_share_percentage}% Contribution Pool</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-cyan-400">
              ${monthlyRevenue?.contribution_pool?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Status</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyRevenue?.is_finalized ? (
              <Badge className="bg-green-600">Finalized</Badge>
            ) : (
              <Badge variant="secondary">Pending</Badge>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Manual Entry</CardTitle>
          </CardHeader>
          <CardContent>
            {!monthlyRevenue?.is_finalized ? (
              <Button 
                size="sm" 
                variant="outline"
                onClick={onOpenManualRevenue}
              >
                <Edit className="w-4 h-4 mr-1" />
                {monthlyRevenue?.total_membership_revenue > 0 ? 'Edit' : 'Enter'} Revenue
              </Button>
            ) : (
              <span className="text-sm text-gray-500">Locked</span>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Payouts List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Payouts - {formatMonth(selectedMonth)}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map(month => (
                    <SelectItem key={month} value={month}>
                      {formatMonth(month)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {!monthlyRevenue?.is_finalized && (
                <Button onClick={onFinalizeRevenue} variant="outline">
                  Finalize Revenue
                </Button>
              )}
              
              {monthlyRevenue?.is_finalized && payouts.length === 0 && (
                <Button onClick={onGeneratePayouts} className="bg-cyan-600">
                  Generate Payouts
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No payouts generated for this month yet.
              {!monthlyRevenue?.is_finalized && ' Finalize revenue first.'}
            </p>
          ) : (
            <div className="space-y-3">
              {payouts.map(payout => (
                <div 
                  key={payout.id}
                  className="flex items-center justify-between p-4 bg-slate-800 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <ProfilePicture src={payout.user?.profile_picture} size="md" />
                    <div>
                      <h4 className="font-medium">{payout.user?.full_name}</h4>
                      <div className="flex items-center gap-3 text-sm text-gray-400">
                        <span>{payout.points_earned} pts</span>
                        <span>•</span>
                        <span>{(payout.share_percentage * 100).toFixed(1)}% share</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-400">
                        ${payout.payout_amount.toFixed(2)}
                      </p>
                      {payout.mailing_info?.mailing_address && (
                        <p className="text-xs text-gray-500">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {payout.mailing_info.mailing_city}, {payout.mailing_info.mailing_state}
                        </p>
                      )}
                    </div>
                    
                    {payout.status === 'pending' ? (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          const checkNum = prompt('Enter check number (optional):');
                          onUpdatePayout(payout.id, 'completed', checkNum);
                        }}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Paid
                      </Button>
                    ) : payout.status === 'completed' ? (
                      <Badge className="bg-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Paid {payout.check_number && `#${payout.check_number}`}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <XCircle className="w-3 h-3 mr-1" />
                        Skipped
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayoutsManager;
